import { useEffect, useRef } from 'react'
import mermaid from 'mermaid'

import Breadcrumbs from '@/components/ui/Breadcrumbs'

// -- Mermaid config ---------------------------------------------------------

mermaid.initialize({
    startOnLoad: false,
    theme: 'base',
    themeVariables: {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '13px',
    },
    flowchart: {
        curve: 'linear',
        padding: 16,
        nodeSpacing: 60,
        rankSpacing: 70,
    },
})

// -- Couleurs par équipe ----------------------------------------------------
// Pink    = Atelier
// Orange  = Logistique
// Blue    = Service client
// Gray    = Terminaux
// Red     = Refusé

const diagram = `flowchart TD
    %% === STYLES ===
    classDef atelier fill:#FDF2F8,stroke:#EC4899,stroke-width:2px,color:#9D174D
    classDef logistique fill:#FFF7ED,stroke:#F97316,stroke-width:2px,color:#9A3412
    classDef service fill:#EFF6FF,stroke:#3B82F6,stroke-width:2px,color:#1E40AF
    classDef terminal fill:#F9FAFB,stroke:#9CA3AF,stroke-width:2px,color:#374151

    %% === TRUNK ===
    WA["Demande reçue"]:::service
    CC["Client contacté<br/><small>optionnel</small>"]:::service
    WS["Livraison à l'atelier<br/><small>EMAIL · DHL</small>"]:::logistique
    RC["Reçu<br/><small>EMAIL</small>"]:::atelier

    WA -->|"« Marquer comme contacté »"| CC
    CC -->|"« Accepter la demande »"| WS
    WA -.->|"« Accepter la demande »"| WS
    WS -->|"« Marquer comme reçu »"| RC

    %% === BRANCHE BOUTIQUE ===
    IS["Déposé en boutique"]:::service
    RI["Prêt en boutique"]:::service

    CC -->|"« Déposé en boutique »"| IS
    WA -.->|"« Déposé en boutique »"| IS
    IS -->|"« Marquer comme reçu »"| RC

    %% === DIAGNOSTIC (commun à tous les types) ===
    WD["Diagnostic horloger"]:::atelier

    RC -->|"« Démarrer le diagnostic »"| WD

    %% === RÉPARATION / QC ===
    RE["En réparation"]:::atelier
    QC["Contrôle qualité"]:::atelier

    %% === SORTIES DIAGNOSTIC ===
    QP["Devis à préparer"]:::service
    QS["Devis envoyé"]:::service
    QA["Devis accepté"]:::atelier
    QR["Devis refusé"]:::atelier

    WD -->|"« Nécessite un devis »"| QP
    WD -->|"« Passer en réparation »"| RE
    WD -->|"« Passer en contrôle qualité »"| QC
    QP -->|"« Marquer le devis comme envoyé »"| QS
    QS -->|"« Devis refusé »"| QR
    QS -->|"« Devis payé »"| QA
    QA -->|"« Passer en réparation »"| RE
    QR -->|"« Préparer le renvoi »"| TS

    RE -->|"« Passer en contrôle qualité »"| QC
    QC -.->|"« Repasser en réparation »"| RE

    %% === SORTIE QC (selon le type) ===
    TS["Renvoi au client"]:::logistique
    RS["Remise en stock"]:::logistique

    QC -->|"« Préparer le renvoi client »"| TS
    QC -->|"« Préparer la remise en stock »"| RS
    QC -->|"« Préparer le renvoi en boutique »"| TS_STORE["Renvoi à la boutique"]:::logistique
    TS_STORE -->|"« Confier au coursier »"| WC["En livraison à la boutique"]:::logistique
    WC -->|"« Marquer comme arrivé »"| RI

    %% === TERMINAUX ===
    FI["Terminé<br/><small>EMAIL · DHL</small>"]:::terminal
    TS -->|"« Imprimer et marquer comme expédié »"| FI
    RS -->|"« Confirmer la remise en stock »"| FI
    RI -->|"« Confirmer la récupération »"| FI


`

// -- Page -------------------------------------------------------------------

export default function ServiceCaseWorkflowPage() {
    const containerRef = useRef(null)

    useEffect(() => {
        let cancelled = false
        const id = `workflow-${Date.now()}`
        const render = async () => {
            if (!containerRef.current) return
            const { svg } = await mermaid.render(id, diagram)
            if (cancelled) return
            containerRef.current.innerHTML = svg

            // Make the SVG responsive
            const svgEl = containerRef.current.querySelector('svg')
            if (svgEl) {
                svgEl.style.maxWidth = '100%'
                svgEl.style.height = 'auto'
            }
        }
        render()
        return () => { cancelled = true }
    }, [])

    const breadcrumbs = [
        { label: 'Service après-vente', href: '/service-cases' },
        { label: 'Workflow' },
    ]

    return (
        <div className="flex flex-col h-screen bg-white">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <div className="flex-1 overflow-auto p-8">
                <div className="mb-6 flex gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                        <span className="inline-block w-4 h-3 rounded-sm border-2 border-pink-400 bg-pink-50" />
                        Atelier
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="inline-block w-4 h-3 rounded-sm border-2 border-orange-400 bg-orange-50" />
                        Logistique
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="inline-block w-4 h-3 rounded-sm border-2 border-blue-400 bg-blue-50" />
                        Service client
                    </div>
                </div>
                <div ref={containerRef} className="flex justify-center max-w-4xl mx-auto" />
            </div>
        </div>
    )
}
